import { useEffect, useState } from "react";

import api from "../api";

import toast from "react-hot-toast";

import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  Legend,
  XAxis,
  YAxis
} from "recharts";

import { getUser } from "../utils/auth";

import {
  Shield,
  User,
  Briefcase,
  TrendingUp,
  Wallet,
  ArrowDownCircle,
  Percent
} from "lucide-react";

export default function Dashboard() {

  const [user, setUser] = useState({
    name: "",
    role: ""
  });

  const [statistiques, setStats] =
    useState({});

  const [previous, setPrevious] =
    useState({});

  const [chart, setChart] =
    useState([]);

  const [forecast, setForecast] =
    useState(0);

  const [growth, setGrowth] =
    useState(0);

  const [alertes, setAlertes] =
    useState([]);

  const [now, setNow] =
    useState(new Date());

  const [sources, setSources] =
    useState([]);

  const [commissionsOTA, setCommissionsOTA] =
    useState([]);

  // =====================
  // DEVISES
  // =====================

  const [recettesUSD, setRecettesUSD] = useState(0);
  const [recettesCDF, setRecettesCDF] = useState(0);

  const [prestationsUSD, setPrestationsUSD] = useState(0);
  const [prestationsCDF, setPrestationsCDF] = useState(0);

  // =====================
  // BENEFICE REEL
  // =====================

  const totalCommissions =
    commissionsOTA.reduce(
      (a, b) =>
        a + Number(b.dette || 0),
      0
    );

  const beneficeNet =
    (statistiques.revenus || 0) -
    (statistiques.depenses || 0) -
    totalCommissions;

  // =====================
  // INIT
  // =====================

  useEffect(() => {

    const currentUser = getUser();

    setUser({
      name:
        currentUser?.nom ||
        "Utilisateur",

      role:
        currentUser?.role || ""
    });

    fetchData();

    const interval = setInterval(
      () => setNow(new Date()),
      1000
    );

    return () =>
      clearInterval(interval);

  }, []);

  // =====================
  // SAFE API
  // =====================

  const safeCall = async (fn) => {

    try {

      return await fn();

    } catch (err) {

      console.error(
        "API ERROR:",
        err?.response?.data ||
          err.message
      );

      return { data: {} };

    }
  };

  const formatMoney = (value) =>
    `${Number(
      value || 0
    ).toLocaleString()} $`;

  const formatUSD = (value) =>
    `${Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })} USD`;

  const formatCDF = (value) =>
    `${Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })} CDF`;

  const detteOTA =
    commissionsOTA.reduce(
      (a, b) =>
        a + Number(b.dette || 0),
      0
    );

  // =====================
  // FETCH DATA
  // =====================

  const fetchData = async () => {

    try {

      // =====================
      // STATISTIQUES
      // =====================

      const today =
        await safeCall(() =>
          api.get(
            "/statistiques/global?periode=jour"
          )
        );

      const prev =
        await safeCall(() =>
          api.get(
            "/statistiques/global?periode=semaine"
          )
        );

      // IMPORTANT :
      // Le Dashboard du comptable affiche les KPI
      // sur le mois en cours.
      const mois =
        await safeCall(() =>
          api.get(
            "/statistiques/global?periode=mois"
          )
        );

      const revenus =
        await safeCall(() =>
          api.get(
            "/statistiques/revenus-mensuels?periode=mois"
          )
        );

      const croissance =
        await safeCall(() =>
          api.get(
            "/statistiques/croissance-mensuelle"
          )
        );

      const alertesRes =
        await safeCall(() =>
          api.get(
            "/statistiques/alertes"
          )
        );


      // =====================
      // KPI DU MOIS
      // =====================

      setStats({

        revenus: Number(
          mois.data?.total_revenus || 0
        ),

        depenses: Number(
          mois.data?.total_depenses || 0
        )

      });


      // =====================
      // ENCAISSEMENTS USD / CDF
      // =====================

      const paiementsRes =
        await safeCall(() =>
          api.get(
            "/revenus?limit=1000"
          )
        );

      const paiements =
        Array.isArray(
          paiementsRes.data?.data
        )
          ? paiementsRes.data.data
          : [];


      // USD
      setRecettesUSD(

        paiements
          .filter(
            (p) =>
              String(
                p.devise || "USD"
              ).toUpperCase() === "USD"
          )
          .reduce(
            (total, p) =>
              total +
              Number(
                p.montant || 0
              ),
            0
          )

      );


      // CDF
      setRecettesCDF(

        paiements
          .filter(
            (p) =>
              String(
                p.devise || ""
              ).toUpperCase() === "CDF"
          )
          .reduce(
            (total, p) =>
              total +
              Number(
                p.montant || 0
              ),
            0
          )

      );


      // =====================
      // PRESTATIONS USD / CDF
      // =====================

      const prestationsRes =
        await safeCall(() =>
          api.get(
            "/blanchisserie/revenus"
          )
        );

      const prestations =
        prestationsRes.data?.revenus || {};

      console.log("PRESTATIONS API :", prestations);
      console.log("PRESTATIONS USD :", prestations.USD);
      console.log("PRESTATIONS CDF :", prestations.CDF);

      setPrestationsUSD(
        Number(
          prestations.USD || 0
        )
      );


      setPrestationsCDF(
        Number(
          prestations.CDF || 0
        )
      );


      // =====================
      // REVENUS MENSUELS
      // =====================

      const revenusMensuels =
        Array.isArray(
          revenus.data
        )
          ? revenus.data
          : (
              revenus.data?.data ||
              []
            );

      // =====================
      // CROISSANCE
      // =====================


      // =====================
      // ALERTES
      // =====================

      setAlertes(
        alertesRes.data || []
      );


      // =====================
      // SOURCES DE REVENUS
      // =====================

      const sourcesRes =
        await safeCall(() =>
          api.get(
            "/statistiques/revenus-sources"
          )
        );

      const sourcesData =
        Array.isArray(
          sourcesRes.data
        )
          ? sourcesRes.data
          : (
              sourcesRes.data?.data ||
              []
            );


      const normalizedSources =
        sourcesData.map(
          (item) => ({

            ...item,

            revenus:
              Number(
                item.revenus ||
                item.total ||
                item.montant ||
                0
              )

          })
        );


      setSources(
        normalizedSources
      );


      // =====================
      // COMMISSIONS OTA
      // =====================

      const commissionsRes =
        await safeCall(() =>
          api.get(
            "/statistiques/revenue-channels"
          )
        );

      const commissionsData =
        commissionsRes.data || {};

      setCommissionsOTA(
        Array.isArray(
          commissionsData.channels
        )
          ? commissionsData.channels
          : []
      );


    } catch (err) {

      console.error(
        "Erreur Dashboard :",
        err
      );

    }

  };

  // =====================
  // VARIATION
  // =====================

  const variation = (a, b) =>
    !b
      ? 0
      : (
          ((a - b) / b) *
          100
        ).toFixed(1);

  // =====================
  // ROLE CONFIG
  // =====================

  const role =
    user.role?.toLowerCase();

  const roleConfig = {
    admin: {
      icon: <Shield size={16} />
    },

    reception: {
      icon: <User size={16} />
    }

  }[role] || {
    icon: <Briefcase size={16} />
  };

  // =====================
  // RENDER
  // =====================

  return (

      <div className="max-w-7xl mx-auto p-3 md:p-6 space-y-4 md:space-y-6 min-h-[100dvh]">

        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">

          <div>

            <h1 className="text-xl md:text-2xl font-bold">
              Bonjour
            </h1>

            <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">

              {roleConfig.icon}

              <span>
                {user.name}
              </span>

            </div>

          </div>

          <div className="text-left md:text-right">

            <div className="text-xs text-gray-400">
              Temps réel
            </div>

            <div className="font-medium">
              {now.toLocaleTimeString()}
            </div>

          </div>

        </div>

        {/* KPI */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3 md:gap-4">

          <Card
            title="Revenus"
            value={formatMoney(
              statistiques.revenus
            )}
            v={variation(
              statistiques.revenus,
              previous.revenus
            )}
            icon={<Wallet />}
          />

          <Card
            title="Dépenses"
            value={formatMoney(
              statistiques.depenses
            )}
            v={variation(
              statistiques.depenses,
              previous.depenses
            )}
            icon={<ArrowDownCircle />}
          />

          <Card
            title="Dette OTA"
            value={formatMoney(
              detteOTA
            )}
            icon={
              <ArrowDownCircle />
            }
          />

          <Card
            title="Bénéfice Net"
            value={formatMoney(
              beneficeNet
            )}
            icon={<TrendingUp />}
          />

          <Card
            title="Croissance"
            value={growth + "%"}
            icon={<Percent />}
          />

        </div>

        {/* =====================
            ENCAISSEMENTS PAR DEVISE
        ===================== */}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">

          <div className="bg-white p-3 md:p-5 rounded-2xl shadow">

            <h3 className="mb-3 font-semibold text-base md:text-lg">
              Encaissements hébergement
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

              <CurrencyCard
                title="USD"
                value={formatUSD(recettesUSD)}
              />

              <CurrencyCard
                title="CDF"
                value={formatCDF(recettesCDF)}
              />

            </div>

          </div>


          {/* =====================
              PRESTATIONS
          ===================== */}

          <div className="bg-white p-3 md:p-5 rounded-2xl shadow">

            <h3 className="mb-3 font-semibold text-base md:text-lg">
              Prestations
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

              <CurrencyCard
                title="USD"
                value={formatUSD(prestationsUSD)}
              />

              <CurrencyCard
                title="CDF"
                value={formatCDF(prestationsCDF)}
              />

            </div>

          </div>

        </div>

        {/* GRAPH */}
        <div className="bg-white p-3 md:p-6 rounded-2xl shadow">

          <h3 className="mb-4 font-semibold text-base md:text-lg">
            Performance financière
          </h3>

          <ResponsiveContainer
            width="100%"
            height={260}
          >

            <LineChart data={chart}>

              <XAxis dataKey="name" />

              <YAxis />

              <Tooltip />

              <Legend />

              <Line
                dataKey="revenus"
                strokeWidth={3}
              />

            </LineChart>

          </ResponsiveContainer>

        </div>

        {/* SOURCES + OTA */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">

          {/* PIE */}
          <div className="bg-white p-3 md:p-5 rounded-2xl shadow">

            <h3 className="mb-3 font-semibold text-base md:text-lg">
              Répartition revenus
            </h3>

            <div className="overflow-x-auto">

              <PieChart
                width={300}
                height={250}
              >

                <Pie
                  data={
                    Array.isArray(
                      sources
                    )
                      ? sources
                      : []
                  }

                  dataKey="total"

                  nameKey="source"

                  outerRadius={80}

                  label
                >

                  {Array.isArray(
                    sources
                  ) &&
                    sources.map(
                      (
                        entry,
                        index
                      ) => (
                        <Cell
                          key={`cell-${index}`}
                        />
                      )
                    )}

                </Pie>

                <Tooltip />

              </PieChart>

            </div>

          </div>

          {/* OTA */}
          <div className="bg-white p-3 md:p-5 rounded-2xl shadow">

            <h3 className="mb-3 font-semibold text-base md:text-lg">
              Commissions OTA
            </h3>

            <div className="max-h-[320px] overflow-y-auto">

              {commissionsOTA.length ===
              0 ? (

                <div className="text-sm text-gray-400">
                  Aucune donnée
                </div>

              ) : (

                commissionsOTA.map(
                  (c, i) => (
                    <div
                      key={i}
                      className="flex justify-between items-center py-3 border-b text-sm gap-4"
                    >

                      <span className="capitalize font-medium">
                        {c.ota_name}
                      </span>

                      <div className="text-right">

                        <div>
                          Dette :{" "}
                          {formatMoney(
                            c.dette
                          )}
                        </div>

                        <div className="text-green-600">
                          Payé :{" "}
                          {formatMoney(
                            c.paye
                          )}
                        </div>

                      </div>

                    </div>
                  )
                )

              )}

            </div>

          </div>

        </div>

      </div>
      
  );
}

function CurrencyCard({
  title,
  value,
  className = ""
}) {
  return (
    <div
      className={`border rounded-xl p-3 md:p-4 ${className}`}
    >
      <div className="text-xs md:text-sm text-gray-400 mb-1">
        {title}
      </div>

      <div className="text-lg md:text-xl font-bold">
        {value}
      </div>
    </div>
  );
}

function Card({
  title,
  value,
  icon,
  v
}) {

  return (
    <div className="bg-white p-3 md:p-5 rounded-2xl shadow">

      <div className="flex justify-between items-center mb-2">

        <span className="text-xs md:text-sm text-gray-400">
          {title}
        </span>

        <div className="scale-90 md:scale-100">
          {icon}
        </div>

      </div>

      <div className="text-lg md:text-2xl font-bold break-words">
        {value}
      </div>

      {v !== undefined && (

        <div
          className={`text-xs mt-2 ${
            v >= 0
              ? "text-green-500"
              : "text-red-500"
          }`}
        >
          {v > 0 ? "+" : ""}
          {v}%
        </div>

      )}

    </div>
  );
}